import {Session} from "../../models/session";
import {useSessionCookie} from "../../hooks/useSessionCookie";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box, Button,
    Card, Chip,
    CircularProgress, ColorPaletteProp, DialogActions,
    DialogTitle,
    Divider, Grid,
    Modal, ModalClose,
    ModalDialog,
    Stack, Tab, tabClasses, TabList, TabPanel, Tabs,
    Typography,
    useTheme
} from "@mui/joy";
import React, {ReactNode, useEffect, useState} from "react";
import {SECTION_LIST, SECTION_NAME_MAP, SectionName} from "../../models/section";
import {
    Award,
    CheckCircle,
    MessageCircleWarning, MessageSquareWarning,
    Share2, TrendingUp
} from "lucide-react";
import {ErrorTag, QuestionAttemptFeedback} from "../../models/error";
import {QuestionAttemptFeedbackDescription} from "../../models/feedback";
import {EXAM_SCORE_FIELD, ExamType, LANGUAGE_TIERS, resolveLanguageTier} from "../../models/exam";
import {ResultCardContent} from "../../utils/resultCard";
import {useTranslation} from "react-i18next";
import {useAsync} from "../../hooks/useAsyncResult";
import {getSession} from "../../services/api/session";
import {useNavigate, useParams} from "react-router-dom";
import {appRoute} from "../../utils/routes";
import LoadingPageComponent from "../Loading/LoadingPageComponent";
import LoadingError from "../Error/LoadingError";
import ShareResultDialog from "./ShareResultDialog";

type TestResultItemComponentProps = {
    session?: Session,
    testTitle?: string,
    open?: boolean,
    onClose?: () => void,
}

const TestResultComponent = (props: TestResultItemComponentProps) => {
    const {t, i18n} = useTranslation()
    const {userSession} = useSessionCookie()
    const [shareDialogOpen, setShareDialogOpen] = useState(false)
    const navigate = useNavigate()
    const {sessionId} = useParams()
    const [session, setSession] = useState<Session>(props.session)
    const [currentSlide, setCurrentSlide] = useState(0)
    const sections: SectionName[] = Object.keys(session?.sectionScores || []) as SectionName[]
    const [qaFeedbackDescription, setQaFeedbackDescription] = useState<QuestionAttemptFeedbackDescription[]>([])
    // Lists (dashboard, results) pass the session in; only /results/:sessionId has to fetch it.
    // Without this check every list card fetched `/sessions/undefined`.
    const fetchesSession = !props.session && !!sessionId
    const sessionAsyncResult = useAsync(getSession, true, false, fetchesSession)
    const score = session?.overallScore
    const errorTagLabelMapper: { [key in ErrorTag]?: string } = {
        article_usage: t("errorTagArticleUsageLabel"),
        coherence: t("errorTagCoherenceLabel"),
        filler_words: t("errorTagFillerWordsLabel"),
        invalid_option: t("errorTagInvalidOptionLabel"),
        off_topic: t("errorTagOffTopicLabel"),
        pronunciation: t("errorTagPronunciationLabel"),
        subject_verb_agreement: t("errorTagSubjectVerbAgreementLabel"),
        tense_confusion: t("errorTagTenseConfusionLabel"),
        word_choice: t("errorTagWordChoiceLabel"),
    }
    const errorSeverityColorMapper: { [key in QuestionAttemptFeedbackDescription['severity']]?: ColorPaletteProp } = {
        high: "danger",
        low: "success",
        medium: "warning",
    }

    // Performance and the result descriptor are the title and description of the
    // same band, so both stay in step with the tier the learner picked at signup.
    const languageTier = resolveLanguageTier(session?.examType, score)

    /** Everything the shareable result card shows, from this session. */
    const shareCardContent = (): ResultCardContent => {
        const examLabelKeys: { [key in ExamType]?: string } = {
            ielts: "ieltsExamLabel", toefl: "toeflExamLabel", tcf_fr: "tcfExamLabel", tcf_ca: "tcfCAExamLabel"
        }
        // TCF Canada sessions report the TCF points score, so use the France points scale.
        const scaleExam: ExamType = session?.examType === "tcf_ca" ? "tcf_fr" : session?.examType
        const scoreField = EXAM_SCORE_FIELD[scaleExam]
        const scoreMax = Math.max(0, ...(LANGUAGE_TIERS[scaleExam]?.tiers || []).map(tier => (tier[scoreField] as { max?: number } | undefined)?.max ?? 0)) || undefined

        // Most frequent feedback tags, in the screen's own labels.
        const topTags = (positive: boolean) => {
            const counts: Record<string, number> = {}
            for (const description of qaFeedbackDescription)
                if (!!description.isPositive === positive && errorTagLabelMapper[description.errorTag])
                    counts[description.errorTag] = (counts[description.errorTag] || 0) + 1
            return Object.keys(counts).sort((a, b) => counts[b] - counts[a]).slice(0, 3).map(tag => errorTagLabelMapper[tag])
        }

        const seconds = session?.endedAt && session?.startedAt
            ? Math.round((session.endedAt - session.startedAt) / 1000)
            : (session?.questionAttempts || []).reduce((total, attempt) => total + (attempt.timeTakenSecs || 0), 0)
        const minutes = Math.round(seconds / 60)
        const timeSpent = minutes >= 60 ? `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")}` : `${Math.max(1, minutes)} min`
        const answered = session?.questionAttempts?.length || 0

        return {
            logoSrc: require("../../assets/full-logo.png"),
            testTitle: session?.testTitle,
            dateLabel: new Date(session?.['createdAt']).toLocaleDateString(i18n.language, {day: "numeric", month: "long", year: "numeric"}),
            learnerName: userSession?.currentUser?.name,
            tags: [
                examLabelKeys[session?.examType] ? t(examLabelKeys[session.examType]) : undefined,
                ...(session?.sections || []).map(section => t(`${section}SectionLabel`)),
                session?.mode === "mock" ? t("mockTestSelectionTitle") : session?.mode ? t("exerciseModeSelectionTitle") : undefined,
            ].filter(Boolean),
            score: String(score ?? "–"),
            scoreValue: score,
            scoreMax,
            scaleLabel: LANGUAGE_TIERS[session?.examType]?.scale,
            performance: languageTier ? t(languageTier.label) : t("resultDescriptorUnavailable"),
            cefr: languageTier?.cefr?.join("–"),
            descriptor: languageTier ? t(languageTier.description) : t("resultDescriptorUnavailable"),
            stats: [
                ...(answered ? [{kind: "questions" as const, value: String(answered), label: t("shareResultQuestionsLabel")}] : []),
                ...(seconds > 0 ? [{kind: "time" as const, value: timeSpent, label: t("shareResultTimeLabel")}] : []),
            ],
            strengths: topTags(true),
            improvements: topTags(false),
            labels: {
                badge: t("shareResultCardBadge"),
                overallScore: t("overallScoreLabel"),
                performance: t("testPerformanceScoreLabel"),
                descriptor: t("resultDescriptorLabel"),
                strengths: t("testResultStrengthsLabel"),
                improvements: t("shareResultImprovementsLabel"),
                footer: t("shareResultCardFooter"),
            }
        }
    }

    const extractFeedback = () => {
        const result: QuestionAttemptFeedbackDescription[] = []
        for (const section of SECTION_LIST) {
            const qaFeedback = session?.feedbackResult?.[section]?.questionAttemptFeedback
            if (!qaFeedback)
                continue

            for (const questionAttemptFeedback of qaFeedback)
                result.push(...questionAttemptFeedback.description)
        }
        setQaFeedbackDescription(result)
    }

    const loadResult = () => {
        sessionAsyncResult.execute(sessionId)
    }

    useEffect(() => {
        if (session)
            extractFeedback()
    }, [session])

    useEffect(() => {
        if (fetchesSession)
            loadResult()
    }, [sessionId])

    useEffect(() => {
        if (sessionAsyncResult.isSuccess)
            setSession(sessionAsyncResult.payload)
    }, [sessionAsyncResult.isSuccess])

    // Opened straight from a notification there's no in-app page to go back to; fall back to the results list.
    const handleClose = () => {
        if (props.onClose) props.onClose()
        else if (window.history.state?.idx > 0) navigate(-1)
        else navigate(appRoute("results"), {replace: true})
    }

    return <Modal open={props.open} onClose={handleClose}>
        <ModalDialog variant="outlined" role="alertdialog"
                     sx={{overflowY: "auto", minWidth: {xs: "100%", md: "60rem"}}}>
            <ModalClose/>

            {!sessionAsyncResult.isSuccess && fetchesSession && !sessionAsyncResult.loading &&
                <LoadingError label={t("resultTitle")} onRetry={loadResult}/>}

            {sessionAsyncResult.loading ?
                <LoadingPageComponent label={t("resultTitle")}/> :
                // Nothing to show until there's a session; a failed load shows only the error above.
                session &&
                <>

                    <DialogTitle>

                        <Stack>
                            <Typography level={"h1"}>
                                {session?.testTitle}
                            </Typography>
                            <Typography level={"body-sm"}>
                                {t("testCompletionDateLabel", {date: new Date(session?.['createdAt']).toLocaleDateString()})}
                            </Typography>
                        </Stack>

                    </DialogTitle>

                    <Divider/>

                    <Tabs
                        aria-label="Pipeline"
                        value={currentSlide}
                        onChange={(event, value) => setCurrentSlide(value as number)}>
                        <TabList
                            sx={{
                                pt: 1,
                                justifyContent: 'center',
                                [`&& .${tabClasses.root}`]: {
                                    flex: 'initial',
                                    bgcolor: 'transparent',
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                    },
                                    [`&.${tabClasses.selected}`]: {
                                        color: 'primary.plainColor',
                                        '&::after': {
                                            height: 2,
                                            borderTopLeftRadius: 3,
                                            borderTopRightRadius: 3,
                                            bgcolor: 'primary.500',
                                        },
                                    },
                                },
                            }}>
                            <Tab indicatorInset>
                                {t("testResultPage1Title")}
                            </Tab>
                            <Tab indicatorInset>
                                {t("testResultPage2Title")}
                            </Tab>
                        </TabList>
                        <Box
                            sx={(theme) => ({
                                '--bg': theme.vars.palette.background.surface,
                                background: 'var(--bg)',
                                boxShadow: '0 0 0 100vmax var(--bg)',
                                clipPath: 'inset(0 -100vmax)',
                            })}>
                            <TabPanel value={0}>
                                <Stack paddingY={2} gap={3}>
                                    <Card sx={{gap: 0}}>
                                        <Typography color={"primary"} startDecorator={<Award/>}>
                                            {t("overallScoreLabel")}
                                        </Typography>
                                        <Typography level={"h1"} fontSize={50}>
                                            {session?.overallScore}
                                        </Typography>
                                    </Card>

                                    <Card sx={{gap: 0}}>
                                        <Typography color={"secondary"} startDecorator={<TrendingUp/>}>
                                            {t("testPerformanceScoreLabel")}
                                        </Typography>
                                        <Typography level={"h1"} fontSize={50}>
                                            {languageTier ? t(languageTier.label) : t("resultDescriptorUnavailable")}
                                        </Typography>
                                    </Card>

                                    <Card sx={{gap: 0}}>
                                        <Typography color={"neutral"} startDecorator={<MessageSquareWarning/>}>
                                            {t("resultDescriptorLabel")}
                                        </Typography>
                                        <Typography level={"title-sm"} fontSize={25}>
                                            {languageTier ? t(languageTier.description) : t("resultDescriptorUnavailable")}
                                        </Typography>
                                    </Card>
                                </Stack>

                            </TabPanel>
                            <TabPanel value={1}>
                                <Stack paddingY={2} gap={3}>

                                    <Grid container spacing={2}>

                                        <Grid xs={12}>
                                            {qaFeedbackDescription.some(value => value.isPositive)
                                                && <Card color={"success"}>
                                                    <Typography color={"success"} startDecorator={<CheckCircle/>}>
                                                        {t("testResultStrengthsLabel")}
                                                    </Typography>


                                                    {sections.map(section =>
                                                        <Stack gap={1}>
                                                            <Grid container spacing={1.5}>
                                                                {session?.feedbackResult[section].questionAttemptFeedback?.map((qaFeedback, index) => {
                                                                    const strengths = qaFeedback.description.filter(value => value.isPositive)
                                                                    const questionLabel = section === "speaking" || section === "writing" ? `Part ${qaFeedback.subsectionNumber}` : `Question ${qaFeedback.questionNumber || index + 1}`

                                                                    return qaFeedback.description.filter(feedback => !!feedback.isPositive).length > 0 &&
                                                                        <Grid xs={12} md={6}>
                                                                            <Accordion variant={"soft"}
                                                                                       sx={{
                                                                                           borderRadius: 10,
                                                                                           padding: 2
                                                                                       }}>
                                                                                <AccordionSummary>
                                                                                    <b> {questionLabel} </b>
                                                                                </AccordionSummary>

                                                                                <AccordionDetails>
                                                                                    <Stack gap={1.5}>
                                                                                        {strengths.map((desc) =>
                                                                                            <Stack
                                                                                                justifyContent={"space-between"}
                                                                                                direction={"row"}
                                                                                                gap={1}>
                                                                                                <Typography
                                                                                                    level={"body-sm"}>
                                                                                                    {desc.message}
                                                                                                </Typography>

                                                                                                <Chip variant={"solid"}
                                                                                                      size={"sm"}
                                                                                                      color={errorSeverityColorMapper[desc.errorTag]}
                                                                                                      sx={{
                                                                                                          textTransform: "capitalize",
                                                                                                          height: "max-content"
                                                                                                      }}>{errorTagLabelMapper[desc.errorTag]}</Chip>
                                                                                            </Stack>)}
                                                                                    </Stack>
                                                                                </AccordionDetails>

                                                                            </Accordion>
                                                                        </Grid>
                                                                })}
                                                            </Grid>
                                                        </Stack>)}

                                                </Card>}
                                        </Grid>

                                        <Grid xs={12}>
                                            {qaFeedbackDescription.some(value => !value.isPositive) &&
                                                <Card color={"danger"}>
                                                    <Typography color={"danger"}
                                                                startDecorator={<MessageCircleWarning/>}>
                                                        {t("testResultWeaknessesLabel")}
                                                    </Typography>
                                                    {sections.map(section =>
                                                        <Stack gap={1}>
                                                            <Grid container spacing={1.5}>
                                                                {session?.feedbackResult[section].questionAttemptFeedback?.map((qaFeedback, index) => {
                                                                        const weaknesses = qaFeedback.description.filter(value => !value.isPositive)
                                                                        const questionLabel = section === "speaking" || section === "writing" ? `Part ${qaFeedback.subsectionNumber}` : `Question ${qaFeedback.questionNumber || index + 1}`

                                                                        if (weaknesses?.length === 0)
                                                                            return <></>

                                                                        return qaFeedbackDescription.filter(value => !value.isPositive).length > 0 &&
                                                                            <Grid xs={12} md={6}>
                                                                                <Accordion variant={"soft"}
                                                                                           sx={{
                                                                                               borderRadius: 10,
                                                                                               padding: 2
                                                                                           }}>

                                                                                    <AccordionSummary>
                                                                                        {questionLabel}
                                                                                    </AccordionSummary>

                                                                                    <AccordionDetails>
                                                                                        <Stack gap={1.5}>
                                                                                            {weaknesses?.map((desc) =>
                                                                                                <Stack
                                                                                                    justifyContent={"space-between"}
                                                                                                    direction={"row"}
                                                                                                    gap={1.5}>

                                                                                                    <Stack>
                                                                                                        <Typography
                                                                                                            level={"body-sm"}>
                                                                                                            {desc.message}
                                                                                                        </Typography>

                                                                                                        {desc.correctAnswer &&
                                                                                                            <Typography
                                                                                                                level={"body-sm"}>
                                                                                                                Correct
                                                                                                                answer
                                                                                                                is <Typography
                                                                                                                level={"body-md"}>{desc.correctAnswer}</Typography>
                                                                                                            </Typography>}
                                                                                                    </Stack>


                                                                                                    {errorTagLabelMapper[desc.errorTag] &&
                                                                                                        <Chip
                                                                                                            variant={"solid"}
                                                                                                            size={"sm"}
                                                                                                            color={errorSeverityColorMapper[desc.errorTag]}
                                                                                                            sx={{
                                                                                                                textTransform: "capitalize",
                                                                                                                height: "max-content"
                                                                                                            }}>{errorTagLabelMapper[desc.errorTag]}</Chip>}
                                                                                                </Stack>)}
                                                                                        </Stack>

                                                                                    </AccordionDetails>

                                                                                </Accordion>
                                                                            </Grid>
                                                                    }
                                                                )}

                                                            </Grid>
                                                        </Stack>)}
                                                </Card>}
                                        </Grid>
                                    </Grid>


                                </Stack>
                            </TabPanel>
                        </Box>
                    </Tabs>

                    <Divider/>

                    <DialogActions sx={{justifyContent: "space-between"}}>

                        <Button size={"lg"} sx={{alignSelf: "flex-end"}} variant={"soft"}
                                color={"secondary"} startDecorator={<Share2/>}
                                disabled={!session} onClick={() => setShareDialogOpen(true)}>
                            {t("shareResultsActionButtonLabel")}
                        </Button>

                        {session &&
                            <ShareResultDialog
                                open={shareDialogOpen}
                                onClose={() => setShareDialogOpen(false)}
                                fileName={`linguascore-${String(session.testTitle || "result").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                content={shareCardContent()}/>}
                    </DialogActions>
                </>}
        </ModalDialog>
    </Modal>
}

export default TestResultComponent